import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Package, ShoppingCart, Euro, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, CATEGORY_LABELS, CATALOGUES, ORDER_STATUS_LABELS } from "@/lib/api";

const EMPTY_FORM = {
  name: "", category: "tshirts_shorts", price: "", description: "",
  image: "", hover_image: "", sizes: "", stock: 0, is_new: false, authentic: false,
};

const STATUS_COLORS = {
  en_attente_paiement: "text-ash",
  payee: "text-volt",
  en_preparation: "text-yellow-400",
  expediee: "text-blue-400",
  livree: "text-green-400",
  annulee: "text-blood",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("produits");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadData = async () => {
    const [s, p, o] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/products"),
      api.get("/admin/orders"),
    ]);
    setStats(s.data);
    setProducts(p.data);
    setOrders(o.data);
  };

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadData().catch(() => {});
      })
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, category: p.category, price: p.price, description: p.description,
      image: p.image, hover_image: p.hover_image || "", sizes: (p.sizes || []).join(", "),
      stock: p.stock, is_new: p.is_new, authentic: !!p.authentic,
    });
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock, 10) || 0,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produit créé");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      loadData().catch(() => {});
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit définitivement ?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Produit supprimé");
      loadData().catch(() => {});
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      toast.success("Statut mis à jour");
      loadData().catch(() => {});
    } catch {
      toast.error("Échec de la mise à jour");
    }
  };

  if (checking) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-ash">Vérification…</p>
      </main>
    );
  }

  return (
    <main className="pt-16 min-h-screen" data-testid="admin-dashboard-view">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-blood mb-2">[ Administration ]</p>
            <h1 className="font-syne font-extrabold uppercase tracking-tighter text-4xl">Tableau de bord</h1>
            {user && <p className="font-mono text-xs text-ash mt-2">{user.email}</p>}
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] hover:border-blood hover:text-blood transition-colors"
          >
            <LogOut size={14} /> Déconnexion
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { icon: Euro, label: "Chiffre d'affaires", value: formatPrice(stats.revenue), testid: "stat-revenue" },
              { icon: ShoppingCart, label: "Commandes", value: stats.orders, testid: "stat-orders" },
              { icon: Package, label: "Produits", value: stats.products, testid: "stat-products" },
              { icon: Clock, label: "À traiter", value: stats.pending, testid: "stat-pending" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="border border-white/10 bg-coal p-5">
                <s.icon size={16} className="text-blood mb-3" />
                <p className="font-syne font-extrabold text-2xl">{s.value}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4">
          {["produits", "commandes"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 border transition-colors ${
                tab === t ? "border-blood bg-blood text-white" : "border-white/15 text-smoke hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "produits" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-ash">{products.length} produits</p>
              <button
                data-testid="admin-new-product-button"
                onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); }}
                className="flex items-center gap-2 bg-volt text-ink font-mono text-xs font-bold uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-white transition-colors"
              >
                <Plus size={14} /> Nouveau produit
              </button>
            </div>

            {showForm && (
              <form data-testid="admin-product-form" onSubmit={submitForm} className="border border-white/15 bg-coal p-6 mb-8 grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2 flex justify-between items-center">
                  <h2 className="font-syne font-bold uppercase tracking-tight">{editingId ? "Modifier le produit" : "Nouveau produit"}</h2>
                  <button type="button" data-testid="admin-form-cancel" onClick={() => setShowForm(false)} className="font-mono text-xs text-ash hover:text-blood uppercase tracking-[0.15em]">Annuler</button>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Nom</label>
                  <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Catégorie</label>
                  <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood">
                    {CATALOGUES.map((c) => (
                      <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Prix (€)</label>
                  <input data-testid="form-price" required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Stock</label>
                  <input data-testid="form-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Tailles (séparées par des virgules)</label>
                  <input data-testid="form-sizes" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="S, M, L, XL"
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] cursor-pointer">
                    <input data-testid="form-is-new" type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })}
                      className="w-4 h-4 accent-[#CCFF00]" />
                    Marquer « Nouveau »
                  </label>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] cursor-pointer">
                    <input data-testid="form-authentic" type="checkbox" checked={form.authentic} onChange={(e) => setForm({ ...form, authentic: e.target.checked })}
                      className="w-4 h-4 accent-[#CCFF00]" />
                    Badge « Authentique »
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Image principale (URL)</label>
                  <input data-testid="form-image" required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Image au survol (URL, optionnel)</label>
                  <input data-testid="form-hover-image" value={form.hover_image} onChange={(e) => setForm({ ...form, hover_image: e.target.value })}
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Description</label>
                  <textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
                </div>
                <button data-testid="admin-form-submit" type="submit"
                  className="md:col-span-2 bg-blood text-white font-mono text-xs uppercase tracking-[0.2em] py-3.5 hover:bg-white hover:text-ink transition-colors">
                  {editingId ? "Enregistrer les modifications" : "Créer le produit"}
                </button>
              </form>
            )}

            <div className="overflow-x-auto border border-white/10">
              <table className="w-full text-sm" data-testid="admin-products-table">
                <thead>
                  <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">
                    <th className="text-left p-4">Produit</th>
                    <th className="text-left p-4">Catégorie</th>
                    <th className="text-left p-4">Prix</th>
                    <th className="text-left p-4">Stock</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={p.image} alt="" className="w-10 h-12 object-cover" />
                          <span className="font-syne font-bold uppercase text-xs">{p.name}</span>
                          {p.is_new && <span className="bg-volt text-ink font-mono text-[9px] font-bold px-1.5 py-0.5 uppercase">New</span>}
                          {p.authentic && <span className="bg-white text-ink font-mono text-[9px] font-bold px-1.5 py-0.5 uppercase ml-1">Auth.</span>}
                        </div>
                      </td>
                      <td className="p-4 font-mono text-xs text-smoke">{CATEGORY_LABELS[p.category] || p.category}</td>
                      <td className="p-4 font-mono text-xs text-volt">{formatPrice(p.price)}</td>
                      <td className="p-4 font-mono text-xs">{p.stock}</td>
                      <td className="p-4 text-right">
                        <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 text-ash hover:text-volt transition-colors" aria-label="Modifier">
                          <Pencil size={15} />
                        </button>
                        <button data-testid={`admin-delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="p-2 text-ash hover:text-blood transition-colors" aria-label="Supprimer">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "commandes" && (
          <div>
            {orders.length === 0 ? (
              <p data-testid="admin-no-orders" className="font-mono text-xs uppercase tracking-[0.2em] text-ash py-16 text-center">
                Aucune commande pour le moment
              </p>
            ) : (
              <div className="space-y-4" data-testid="admin-orders-list">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-white/10 bg-coal p-5">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <div>
                        <p className="font-mono text-xs text-ash">
                          #{o.id.slice(0, 8).toUpperCase()} — {new Date(o.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </p>
                        <p className="font-syne font-extrabold text-xl mt-1">{formatPrice(o.total)}</p>
                      </div>
                      <select
                        data-testid={`order-status-${o.id}`}
                        value={o.status}
                        onChange={(e) => updateStatus(o.id, e.target.value)}
                        className={`bg-ink border border-white/15 px-3 py-2 font-mono text-xs uppercase tracking-[0.1em] focus:outline-none focus:border-blood ${STATUS_COLORS[o.status] || ""}`}
                      >
                        {Object.entries(ORDER_STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      {o.items.map((i, idx) => (
                        <p key={idx} className="font-mono text-xs text-smoke">
                          {i.quantity} × {i.name} — Taille {i.size} — {formatPrice(i.price * i.quantity)}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
